/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState, useCallback } from "react";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../../utility/errorCodes";
import { getPresenceIcon } from "../../utility/ms-teams";
import { toSentenceCase } from "../../utility/index";
import { CopyButton } from "../Common/CopyButton";
import { LoadingButton } from "../Common/LoadingButton";

import "./ExternalContactCard.css";
import useAgentStats from "../../hooks/useAgentStats";
import { CONTACT_TYPES, PII_FIELDS } from "../../../../utility/constants";
import { redactPII } from "../../../../utility/helpers";

const COMPONENT_NAME = "CCP components/ExternalTransfers/ExternalContactCard";

/**
 * Displays external contact details with Teams availability
 */
export const ExternalContactCard = ({
	externalContact,
	onStartTransfer,
	onStartTeamsChat,
	refreshDisplayedActivity,
	transfersEnabled,
}) => {
	const [confirmMessage, setConfirmMessage] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [isStartingTeamsChat, setIsStartingTeamsChat] = useState(false);
	const { agentStats } = useAgentStats();

	let teamsMessagingEnabled = true;
	let startChatTitle = "Start a Teams conversation";
	const { conversationId, hasBotInstalled, inActiveConversation } = externalContact.teamsInfo;
	const { configuredCapacity, currentCapacity } = agentStats;

	if (!conversationId || !hasBotInstalled) {
		teamsMessagingEnabled = false;
		startChatTitle = "Unavailable for this external contact";
	} else if (inActiveConversation) {
		teamsMessagingEnabled = false;
		startChatTitle = "External contact in active conversation with another agent";
	} else if (currentCapacity[CONTACT_TYPES.CHAT] === configuredCapacity[CONTACT_TYPES.CHAT]) {
		teamsMessagingEnabled = false;
		startChatTitle = "Unavailable capacity for starting a new chat";
	} else if (currentCapacity[CONTACT_TYPES.VOICE] > 0 || currentCapacity[CONTACT_TYPES.TASK] > 0) {
		teamsMessagingEnabled = false;
		startChatTitle = "Unavailable while on a voice/task contact";
	}

	const handleTransfer = useCallback(
		async (skipActivityCheck = false) => {
			try {
				setConfirmMessage("");
				setIsLoading(true);
				const updatedContacts = await refreshDisplayedActivity();
				const newActivity = updatedContacts.find((c) => c.contactId === externalContact.contactId).activity;
				if (!skipActivityCheck && newActivity !== "Available" && externalContact.azureAdUserId) {
					setConfirmMessage(
						`Contact's status is currently '${toSentenceCase(
							newActivity || "PresenceUnknown",
						)}' and they may not be able to accept the call.`,
					);
				} else {
					await onStartTransfer(externalContact);
				}
			} catch (error) {
				v2.error("Error while transferring to external contact.", {
					additionalData: {
						error: errorToObject(error),
						externalContact: redactPII(externalContact, PII_FIELDS.externalContact),
					},
					componentName: COMPONENT_NAME,
					logCode: errorCodes.START_EXTERNAL_TRANSFER_ERROR,
				});
			}
			setIsLoading(false);
		},
		[refreshDisplayedActivity, externalContact, onStartTransfer],
	);

	const handleStartTeamsChat = async () => {
		setIsStartingTeamsChat(true);
		await onStartTeamsChat(externalContact);
		setIsStartingTeamsChat(false);
	};

	return (
		<div
			className="d-flex align-items-center external-contact-card justify-content-between"
			style={{ flexWrap: "wrap" }}
		>
			{confirmMessage ? (
				<div className="d-flex align-items-center justify-content-between w-100">
					<div>
						{confirmMessage} <span style={{ fontWeight: "600" }}>Do you want to proceed?</span>
					</div>
					<div className="ml-2 d-flex">
						<LoadingButton
							text="No"
							isLoading={false}
							variant="outline-secondary"
							size="sm"
							onClick={() => setConfirmMessage("")}
						/>
						<LoadingButton
							text="Yes"
							isLoading={false}
							variant="outline-secondary"
							size="sm"
							onClick={() => handleTransfer(true)}
							className="ml-2"
						/>
					</div>
				</div>
			) : (
				<>
					<div className="d-flex align-items-center" style={{ fontSize: "18px", flexWrap: "wrap", flex: 1 }}>
						<div className="d-flex align-items-center">
							{externalContact.activity ? (
								<img
									src={getPresenceIcon(externalContact.activity)}
									title={toSentenceCase(externalContact.activity)}
									alt="User presence"
									style={{ width: 14, height: 14 }}
								/>
							) : (
								<span style={{ width: 14, height: 14, display: "inline-block" }}></span>
							)}
							<span style={{ fontWeight: "600", marginLeft: 8, display: "inline-block" }}>
								{externalContact.fullName}
							</span>
						</div>
						<span style={{ marginLeft: 20, display: "flex", flex: 1 }}>
							{externalContact.tags.map((tag, i) => (
								<span className="ccp-chip mr-1" key={tag + i}>
									{tag}
								</span>
							))}
						</span>
						{externalContact.phone && (
							<div style={{ display: "flex", marginLeft: 12 }}>
								<span className="ml-2">{externalContact.phone}</span>
								<span className="ml-1 mr-2">
									<CopyButton value={externalContact.phone} title="copy phone number" />
								</span>
							</div>
						)}
					</div>
					<LoadingButton
						text="Transfer"
						isLoading={isLoading}
						variant="outline-secondary"
						disabled={!externalContact.phone || !transfersEnabled}
						title={transfersEnabled ? "Transfer caller to this contact." : "This contact cannot be transferred."}
						size="sm"
						className="ml-2"
						onClick={() => handleTransfer()}
					/>
					<LoadingButton
						text="Start Chat"
						isLoading={isStartingTeamsChat}
						variant="outline-secondary"
						disabled={!teamsMessagingEnabled}
						title={startChatTitle}
						size="sm"
						className="ml-2"
						onClick={() => handleStartTeamsChat()}
					/>
				</>
			)}
		</div>
	);
};
